import { createTRPCClient, httpLink, splitLink } from '@trpc/client'
import { createTRPCReact } from '@trpc/react-query'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import { useQueryClient } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'api/src/types'
import SuperJSON from 'superjson'
import { useMemo } from 'react'
import { adminAuthHeaders, fetchWithAdminAuth } from './admin-auth'
import { API_URL } from './constants'

type RouterOutputs = inferRouterOutputs<AppRouter>

export type GamePassGrant = RouterOutputs['promoGamePassRouter']['adminGrant']

export type GameplayGuidance = RouterOutputs['queryRouter']['getGameplayGuidance']['guidance']

export type ModelCatalogDiff = NonNullable<RouterOutputs['queryRouter']['getLastCatalogDiff']['data']>
export type ModelProbeReport = RouterOutputs['queryRouter']['probeModels']['data'][number]
export type GameDataResponse = RouterOutputs['queryRouter']['getGameDataById']
export type GamesByFiltersResponse = RouterOutputs['queryRouter']['getGamesByFilters']
export type FilterOptionsResponse = RouterOutputs['queryRouter']['getFilterOptions']
export type StimulateGamesBatchResponse = RouterOutputs['batchRouter']['stimulateGamesBatch']
export type EmailWaveSummary = RouterOutputs['batchRouter']['emailWaveSummaries']['waves'][number]

export const trpc = createTRPCReact<AppRouter>()

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      // Complete unsaved bundles are too large for query-string transport.
      condition: operation => operation.path === 'queryRouter.previewGameplayGuidance',
      true: httpLink({
        url: `${API_URL}/api`,
        transformer: SuperJSON,
        headers: adminAuthHeaders,
        fetch: fetchWithAdminAuth,
        methodOverride: 'POST',
      }),
      false: httpLink({
        url: `${API_URL}/api`,
        transformer: SuperJSON,
        headers: adminAuthHeaders,
        fetch: fetchWithAdminAuth,
      }),
    }),
  ],
})

export function useTRPC() {
  const queryClient = useQueryClient()
  return useMemo(
    () => createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient }),
    [queryClient],
  )
}
